import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { skuById } from "@/data/products";

const CartContext = createContext(null);
const STORAGE_KEY = "relax_cart_v1";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch {
      return {};
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [promo, setPromo] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("relax_promo_v1")) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (promo) localStorage.setItem("relax_promo_v1", JSON.stringify(promo));
    else localStorage.removeItem("relax_promo_v1");
  }, [promo]);

  const addItem = useCallback((variantId, qty = 1) => {
    setItems((prev) => ({ ...prev, [variantId]: (prev[variantId] || 0) + qty }));
  }, []);

  const setQty = useCallback((variantId, qty) => {
    setItems((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[variantId];
      else next[variantId] = qty;
      return next;
    });
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems((prev) => {
      const next = { ...prev };
      delete next[variantId];
      return next;
    });
  }, []);

  const clearCart = useCallback(() => setItems({}), []);

  const cartLines = useMemo(
    () =>
      Object.entries(items)
        .map(([id, qty]) => ({ variant: skuById(id), qty }))
        .filter((l) => l.variant),
    [items]
  );

  const subtotal = useMemo(
    () => cartLines.reduce((sum, l) => sum + l.variant.price * l.qty, 0),
    [cartLines]
  );
  const count = useMemo(() => cartLines.reduce((sum, l) => sum + l.qty, 0), [cartLines]);

  const discount = useMemo(
    () => (promo ? Math.round(subtotal * promo.percent) / 100 : 0),
    [promo, subtotal]
  );

  return (
    <CartContext.Provider
      value={{ items, cartLines, subtotal, count, promo, setPromo, discount, addItem, setQty, removeItem, clearCart, drawerOpen, setDrawerOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
